import {
  redirectActions,
  TEMPLATE_EDITOR_PAGE_ROUTE,
  TEMPLATES_TEAM_ROUTE,
} from '../../../entities/history';
import { templatesModel } from '../../../features/templates';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router-dom';
import { TemplateExtended } from '@distribute/shared/api-types/templates';

import { useTemplatesActions } from './use-templates-actions';
import { useCallback, useMemo } from 'react';
import { IconMap } from '../../../shared/sprite';
import { DropdownItem } from '../../../shared/ui/Dropdown';

import {
  TemplateCreationPhase,
  TemplateSharingAccess,
} from '@distribute/shared/types';

import { useShareLink } from '../../../entities/share';
import { usePreviewTemplate } from './use-preview-template';

type Params = {
  template: TemplateExtended;
  handleOpenDeleteTemplateModal: (template: TemplateExtended) => void;
  handleOpenTemplateSettingsModal: (template: TemplateExtended) => void;
};

export const useTemplateActions = ({
  template,
  handleOpenDeleteTemplateModal,
  handleOpenTemplateSettingsModal,
}: Params) => {
  const { handleCopyShareLink } = useShareLink();
  const { handleTemplatePreview } = usePreviewTemplate();

  const isTeam = template.sharingAccess === TemplateSharingAccess.TEAM;
  const isDraft =
    template.creationPhase === TemplateCreationPhase.DRAFT && template.isOwner;

  const { handleClickChooseTemplate, createPageIsLoading } =
    useTemplatesActions();

  const dispatch = useDispatch();

  const handleTemplateEdit = useCallback(() => {
    dispatch(
      redirectActions.fromWorkspaceWithBackUrl({
        path: generatePath(TEMPLATE_EDITOR_PAGE_ROUTE, {
          sequenceNumber: template.id,
        }),
      })
    );
  }, [dispatch, template.id]);

  const handleTemplateDuplicate = useCallback(() => {
    dispatch(
      templatesModel.actions.duplicateTemplate({
        templateId: template.id,
      })
    );
  }, [dispatch, template.id]);

  const handleTemplateSelect = useCallback(() => {
    dispatch(templatesModel.actions.setCurrentSelectedTemplate(template));
    handleClickChooseTemplate();
  }, [dispatch, handleClickChooseTemplate, template]);

  const templateOptions = useMemo<DropdownItem[]>(() => {
    const shareOption: DropdownItem[] = isTeam
      ? [
          {
            id: 'share',
            label: 'Share with team',
            onClick: () =>
              handleCopyShareLink({
                path: TEMPLATES_TEAM_ROUTE,
                id: `${template.id}`,
              }),
            iconName: IconMap.Share07,
            iconWidth: 16,
          },
        ]
      : [];

    if (template.isOwner) {
      if (isDraft) {
        return [
          {
            id: 'Edit',
            label: 'Edit',
            onClick: handleTemplateEdit,
            iconName: IconMap.MenuEdit,
            iconWidth: 16,
          },
          {
            id: 'Duplicate',
            label: 'Duplicate',
            onClick: handleTemplateDuplicate,
            iconName: IconMap.MenuCopy,
            iconWidth: 16,
          },
          {
            id: 'Settings',
            label: 'Settings',
            onClick: () => handleOpenTemplateSettingsModal(template),
            iconName: IconMap.Settings,
            iconWidth: 16,
          },
          {
            id: 'Delete',
            label: 'Delete',
            onClick: () => handleOpenDeleteTemplateModal(template),
            iconName: IconMap.Delete,
            iconWidth: 16,
          },
        ];
      }
      return [
        {
          id: 'UseTemplate',
          label: 'Use template',
          onClick: handleTemplateSelect,
          iconName: IconMap.FileCheck03,
          iconWidth: 16,
          disabled: createPageIsLoading,
        },
        {
          id: 'Preview',
          label: 'Preview',
          onClick: () => handleTemplatePreview(template),
          iconName: IconMap.Eye,
          iconWidth: 16,
        },
        ...shareOption,
        {
          id: 'Edit',
          label: 'Edit',
          onClick: handleTemplateEdit,
          iconName: IconMap.MenuEdit,
          iconWidth: 16,
          isSeparatedFromTop: true,
        },
        {
          id: 'Duplicate',
          label: 'Duplicate',
          onClick: handleTemplateDuplicate,
          iconName: IconMap.MenuCopy,
          iconWidth: 16,
        },
        {
          id: 'Settings',
          label: 'Settings',
          onClick: () => handleOpenTemplateSettingsModal(template),
          iconName: IconMap.Settings,
          iconWidth: 16,
        },
        {
          id: 'Delete',
          label: 'Delete',
          onClick: () => handleOpenDeleteTemplateModal(template),
          iconName: IconMap.Delete,
          iconWidth: 16,
        },
      ];
    }

    return [
      {
        id: 'UseTemplate',
        label: 'Use template',
        onClick: handleTemplateSelect,
        iconName: IconMap.FileCheck03,
        iconWidth: 16,
        disabled: createPageIsLoading,
      },
      {
        id: 'Preview',
        label: 'Preview',
        onClick: () => handleTemplatePreview(template),
        iconName: IconMap.Eye,
        iconWidth: 16,
      },
      ...shareOption,
    ];
  }, [
    template,
    handleOpenTemplateSettingsModal,
    handleOpenDeleteTemplateModal,
    handleTemplateSelect,
    handleTemplatePreview,
    handleTemplateDuplicate,
    handleTemplateEdit,
    handleCopyShareLink,
    isDraft,
    isTeam,
    createPageIsLoading,
  ]);

  return {
    handleTemplateEdit,
    handleTemplatePreview,
    handleTemplateSelect,
    templateOptions,
    isDraft,
  };
};
